import { useState, useCallback } from 'react';

interface GeolocationState {
  latitude: number | null;
  longitude: number | null;
  accuracy: number | null;
  error: string | null;
  isLoading: boolean;
}

interface UseGeolocationReturn extends GeolocationState {
  getCurrentPosition: () => Promise<GeolocationCoordinates | null>;
  clearError: () => void;
}

const GEOLOCATION_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  timeout: 15000,
  maximumAge: 0,
};

function getErrorMessage(error: GeolocationPositionError): string {
  switch (error.code) {
    case error.PERMISSION_DENIED:
      return 'Permissão de localização negada. Habilite nas configurações do navegador.';
    case error.POSITION_UNAVAILABLE:
      return 'Localização indisponível. Verifique se o GPS está ativado.';
    case error.TIMEOUT:
      return 'Tempo esgotado ao obter localização. Tente novamente.';
    default:
      return 'Erro desconhecido ao obter localização.';
  }
}

export function useGeolocation(): UseGeolocationReturn {
  const [state, setState] = useState<GeolocationState>({
    latitude: null,
    longitude: null,
    accuracy: null,
    error: null,
    isLoading: false,
  });

  const getCurrentPosition = useCallback((): Promise<GeolocationCoordinates | null> => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        setState((prev) => ({
          ...prev,
          error: 'Geolocalização não suportada pelo navegador.',
          isLoading: false,
        }));
        resolve(null);
        return;
      }

      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude, accuracy } = position.coords;
          setState({
            latitude,
            longitude,
            accuracy,
            error: null,
            isLoading: false,
          });
          resolve(position.coords);
        },
        (error) => {
          const message = getErrorMessage(error);
          setState((prev) => ({
            ...prev,
            error: message,
            isLoading: false,
          }));
          resolve(null);
        },
        GEOLOCATION_OPTIONS
      );
    });
  }, []);

  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  return {
    ...state,
    getCurrentPosition,
    clearError,
  };
}
